/**
 * Chart backfill/todayfill REST service handler.
 *
 * Manages the two-phase historical data loading strategy for each symbol
 * that is opened in a chart panel:
 *
 * 1. **Phase 1 — Todayfill** (`GET /todayfill/{scripId}`):
 *    Fetches today's intraday 1-minute bars quickly so the chart can render
 *    almost immediately after a symbol is selected.
 *
 * 2. **Phase 2 — Backfill** (`POST /backfill`):
 *    Fetches historical 1-minute bars for the user-configured period (default
 *    30 days) in a single request using `backfillPeriod`. All data arrives in
 *    one response — no server-side pagination needed.
 *
 * Both phases normalise the server response into `OneMinuteBar[]` regardless
 * of whether the server returns array-of-arrays or array-of-objects format.
 * Bars that share the same minute timestamp are aggregated (e.g. per-tick
 * data from the server becomes proper OHLCV candles).
 */
import axios from "@interceptors/index";
import { OneMinuteBar, DayBar } from "../types/chart.types";
import { loadBackfillDays } from "./local-storage-handler";

class ChartBackfillHandler {
  /**
   * Phase 1: Fetch today's intraday 1-min bars (fast).
   * Called first so chart can render quickly.
   */
  public async fetchTodayData(
    scripId: number,
    symbol: string
  ): Promise<OneMinuteBar[]> {
    console.log(`[Chart] ===== fetchTodayData START: ${symbol} (id=${scripId}) =====`);
    try {
      const result = await this.fetchTodayfill(scripId);
      console.log(`[Chart] ===== fetchTodayData END: ${symbol}: ${result.bars.length} bars =====`);
      return result.bars;
    } catch (error: any) {
      console.error(`[Chart] Todayfill FAILED for ${symbol}:`, error);
      return [];
    }
  }

  /**
   * Phase 2: Fetch first page of historical 1-min bars (30 days per page).
   * Called after todayfill is already rendered on chart.
   * Returns bars and a flag indicating whether more pages are available.
   */
  public async fetchHistoryData(
    scripId: number,
    symbol: string
  ): Promise<{ bars: OneMinuteBar[]; hasMore: boolean }> {
    console.log(`[Chart] ===== fetchHistoryData START: ${symbol} (id=${scripId}) =====`);
    try {
      const result = await this.fetchBackfillPage(scripId, 1);
      console.log(`[Chart] ===== fetchHistoryData END: ${symbol}: ${result.bars.length} bars, hasMore=${result.hasMore} =====`);
      return { bars: result.bars, hasMore: result.hasMore };
    } catch (error: any) {
      console.error(`[Chart] Backfill FAILED for ${symbol}:`, error);
      return { bars: [], hasMore: false };
    }
  }

  /**
   * Fetch a specific page of historical backfill data.
   * Called by the scroll handler when the user scrolls towards older data.
   */
  public async fetchNextPage(
    scripId: number,
    symbol: string,
    pageNo: number
  ): Promise<{ bars: OneMinuteBar[]; hasMore: boolean }> {
    console.log(`[Chart] ===== fetchNextPage START: ${symbol} page=${pageNo} =====`);
    try {
      const result = await this.fetchBackfillPage(scripId, pageNo);
      console.log(`[Chart] ===== fetchNextPage END: ${symbol} page=${pageNo}: ${result.bars.length} bars, hasMore=${result.hasMore} =====`);
      return { bars: result.bars, hasMore: result.hasMore };
    } catch (error: any) {
      console.error(`[Chart] Backfill page ${pageNo} FAILED for ${symbol}:`, error);
      return { bars: [], hasMore: false };
    }
  }

  /**
   * POST backfill  { scripsID: <number>, backfillPeriod: <string>, candleInterval: "1M" }
   * The server returns all requested days in one response — no server-side pagination.
   */
  private async fetchBackfillPage(scripId: number, _pageNo: number): Promise<{ bars: OneMinuteBar[]; hasMore: boolean }> {
    const backfillDays = loadBackfillDays();
    const payload = {
      candleInterval: "1M",
      backfillPeriod: String(backfillDays),
      scripsID: scripId,
    };
    console.log("[Chart] POST backfill payload:", JSON.stringify(payload));
    const response = await axios.post("backfill", JSON.stringify(payload), {
      headers: { "Content-Type": "application/json" },
      timeout: 120000,
    });
    const preview = JSON.stringify(response.data)?.substring(0, 300) ?? "null";
    console.log("[Chart] Backfill raw response.data:", preview);

    // Detect "no data available" — server may return a message string,
    // an object with a message field, or simply an empty array.
    if (this.isNoDataResponse(response.data)) {
      console.log(`[Chart] Backfill: no data available`);
      return { bars: [], hasMore: false };
    }

    const bars = this.parseResponse("backfill", response.data);
    // The server returns all requested history in one shot — no further pages.
    return { bars, hasMore: false };
  }

  /**
   * Detect "no data available" responses from the server.
   * The server may return a plain string, or an object with a message field.
   */
  private isNoDataResponse(data: any): boolean {    if (!data) return true;
    if (typeof data === "string") {
      return data.toLowerCase().includes("no data");
    }
    if (typeof data === "object" && !Array.isArray(data)) {
      const msg = data.message ?? data.Message ?? data.msg ?? data.error ?? data.status ?? "";
      if (typeof msg === "string" && msg.toLowerCase().includes("no data")) {
        return true;
      }
      // Empty object with no array values
      const hasArrayData = Object.values(data).some(v => Array.isArray(v) && (v as any[]).length > 0);
      if (!hasArrayData && !Array.isArray(data)) {
        // Check if there's any meaningful data
        const keys = Object.keys(data);
        if (keys.length === 0) return true;
        if (keys.length === 1 && (keys[0] === "message" || keys[0] === "msg" || keys[0] === "status")) return true;
      }
    }
    return false;
  }

  /**
   * GET todayfill/{scripId}
   */
  public async fetchTodayfill(scripId: number): Promise<{ bars: OneMinuteBar[]; debug: string }> {
    console.log(`[Chart] GET todayfill/${scripId}`);
    const response = await axios.get(`todayfill/${scripId}`, {
      timeout: 60000,
    });
    const preview = JSON.stringify(response.data)?.substring(0, 300) ?? "null";
    console.log("[Chart] Todayfill raw response.data:", preview);
    const bars = this.parseResponse("todayfill", response.data);
    const debug = bars.length > 0 ? `${bars.length} bars` : `0 bars | raw: ${preview}`;
    return { bars, debug };
  }

  /**
   * Fetch backfill data for a given number of days and candle interval.
   * Used by the AmiBroker Watchlist Modal which lets the user pick
   * the period and candle type explicitly.
   *
   * POST backfill { candleInterval, backfillPeriod, scripsID }
   */
  public async fetchBackfillForDays(
    scripId: number,
    backfillPeriod: string,
    candleInterval: string = "1M",
  ): Promise<OneMinuteBar[]> {
    const payload = { candleInterval, backfillPeriod, scripsID: scripId };
    const response = await axios.post("backfill", JSON.stringify(payload), {
      headers: { "Content-Type": "application/json" },
      timeout: 120000,
    });
    if (this.isNoDataResponse(response.data)) return [];
    return this.parseResponse("backfill", response.data);
  }

  /**
   * Parse the response — handles whatever shape the server returns.
   * Logs a detailed breakdown so the error message can reveal exactly what was received.
   */
  private parseResponse(label: string, raw: any): OneMinuteBar[] {
    console.log(`[Chart] parseResponse(${label}): typeof=${typeof raw}, isArray=${Array.isArray(raw)}, keys=${raw && typeof raw === "object" ? JSON.stringify(Object.keys(raw)) : "N/A"}`);

    let dataArray: any[] | null = null;

    if (Array.isArray(raw)) {
      dataArray = raw;
      console.log(`[Chart] ${label}: direct array, length=${raw.length}`);
    } else if (raw && typeof raw === "object") {
      // Try every possible envelope field name
      const candidates = ["data", "Data", "candles", "Candles", "ohlcv", "result", "Result", "response", "bars", "Bars", "records"];
      for (const key of candidates) {
        if (Array.isArray(raw[key])) {
          dataArray = raw[key];
          console.log(`[Chart] ${label}: found data under key "${key}", length=${dataArray!.length}`);
          break;
        }
      }
      if (!dataArray) {
        // Last resort: check if any value is an array
        for (const [k, v] of Object.entries(raw)) {
          if (Array.isArray(v) && (v as any[]).length > 0) {
            dataArray = v as any[];
            console.log(`[Chart] ${label}: found array under key "${k}", length=${dataArray.length}`);
            break;
          }
        }
      }
    }

    if (!dataArray || dataArray.length === 0) {
      const preview = typeof raw === "string" ? raw.substring(0, 200) : JSON.stringify(raw)?.substring(0, 200);
      console.warn(`[Chart] ${label}: NO DATA. Full raw preview: ${preview}`);
      return [];
    }

    const sample = dataArray[0];
    console.log(`[Chart] ${label}: sample[0] isArray=${Array.isArray(sample)}, value=${JSON.stringify(sample)}`);
    return this.convertBars(dataArray);
  }

  /**
   * Convert raw OHLCV items to OneMinuteBar.
   *
   * Handles TWO formats from this server:
   *
   * 1. Array-of-arrays (what backfill actually returns):
   *    ["2025-12-29 09:00:00", "5182.0", "5190.0", "5180.0", "5183.0", "40", "18688"]
   *     index:  0=datetime   1=open     2=high     3=low      4=close    5=vol  6=OI
   *
   * 2. Array-of-objects (fallback):
   *    { timestamp/time/date, open/Open/o, high/High/h, low/Low/l, close/Close/c, volume/vol/v }
   */
  private convertBars(data: any[]): OneMinuteBar[] {
    const rawBars = data.map(item => {
      let ts: number, open: number, high: number, low: number, close: number, vol: number, oi: number;

      if (Array.isArray(item)) {
        // ── Array format: [datetime, open, high, low, close, volume, OI?] ──
        const rawTs = item[0];
        if (typeof rawTs === "string") {
          // "2025-12-29 09:00:00" — treat as IST, but Date.parse handles it as local
          // Replace space with T so it parses across all JS engines
          ts = new Date(rawTs.replace(" ", "T")).getTime();
          if (isNaN(ts)) {
            // Fallback: try direct parse
            ts = Date.parse(rawTs);
          }
        } else {
          ts = typeof rawTs === "number"
            ? (rawTs >= 1e10 ? rawTs : rawTs * 1000)
            : 0;
        }
        open  = parseFloat(item[1]);
        high  = parseFloat(item[2]);
        low   = parseFloat(item[3]);
        close = parseFloat(item[4]);
        vol   = parseInt(String(item[5] ?? 0), 10);
        oi    = parseInt(String(item[6] ?? 0), 10);
      } else {
        // ── Object format ──
        const rawTs =
          item.timestamp ?? item.Timestamp ??
          item.time ?? item.Time ??
          item.ts   ?? item.Ts   ??
          item.dateTime ?? item.DateTime ??
          item.date ?? item.Date ??
          item.t    ?? 0;

        if (typeof rawTs === "string") {
          ts = new Date(rawTs.replace(" ", "T")).getTime();
        } else {
          ts = rawTs >= 1e10 ? rawTs : (rawTs * 1000);
        }
        open  = parseFloat(item.open  ?? item.Open  ?? item.o ?? item.O ?? 0);
        high  = parseFloat(item.high  ?? item.High  ?? item.h ?? item.H ?? 0);
        low   = parseFloat(item.low   ?? item.Low   ?? item.l ?? item.L ?? 0);
        close = parseFloat(item.close ?? item.Close ?? item.c ?? item.C ?? 0);
        vol   = parseInt(String(item.volume ?? item.Volume ?? item.vol ?? item.v ?? 0), 10);
        oi    = parseInt(String(item.oi ?? item.OI ?? item.openInterest ?? item.OpenInterest ?? 0), 10);
      }

      // Floor timestamp to minute boundary for consistent deduplication
      const minuteTs = Math.floor(ts / 60000) * 60000;
      return { timestamp: minuteTs, open, high, low, close, volume: isNaN(vol) ? 0 : vol, oi: isNaN(oi) ? 0 : oi, tickCount: 0 };
    }).filter(b => {
      if (!b.timestamp || b.timestamp <= 0 || isNaN(b.timestamp)) return false;
      if (b.open == null || b.high == null || b.low == null || b.close == null) return false;
      if (isNaN(b.open) || isNaN(b.high) || isNaN(b.low) || isNaN(b.close)) return false;
      if (b.open <= 0 || b.close <= 0) return false;
      return true;
    });

    // ── Aggregate bars that share the same minute timestamp ────────────
    // If the server returns per-tick data (each row has O=H=L=C=LTP),
    // multiple rows map to the same minute. We must aggregate them into
    // proper OHLC candles: open from first tick, close from last tick,
    // high = max of all, low = min of all.
    const aggregated = new Map<number, OneMinuteBar>();
    for (const bar of rawBars) {
      const existing = aggregated.get(bar.timestamp);
      if (existing) {
        existing.high = Math.max(existing.high, bar.high);
        existing.low = Math.min(existing.low, bar.low);
        existing.close = bar.close; // last row's close = candle close
        existing.volume += bar.volume;
        if (bar.oi) existing.oi = bar.oi; // last OI value for the minute
      } else {
        aggregated.set(bar.timestamp, { ...bar });
      }
    }
    const bars = Array.from(aggregated.values()).sort((a, b) => a.timestamp - b.timestamp);

    console.log(`[Chart] convertBars: ${data.length} raw → ${rawBars.length} valid → ${bars.length} aggregated bars`);
    if (bars.length > 0) {
      const first = new Date(bars[0].timestamp).toISOString();
      const last  = new Date(bars[bars.length - 1].timestamp).toISOString();
      const sample = bars[Math.min(5, bars.length - 1)];
      console.log(`[Chart] Date range: ${first}  →  ${last}`);
      console.log(`[Chart] Sample bar OHLC: O=${sample.open} H=${sample.high} L=${sample.low} C=${sample.close} spread=${sample.high - sample.low}`);
    }
    return bars;
  }

  // ─── EOD (End-of-Day) Backfill ─────────────────────────────────────────────

  /**
   * Map numeric duration (days) to the server's backfillPeriod string.
   */
  private mapBackfillPeriod(days: number): string {
    if (days >= 7300) return "20Y";
    if (days >= 3650) return "10Y";
    if (days >= 1825) return "5Y";
    return "1Y";
  }

  /**
   * Fetch daily EOD bars for a symbol.
   * Uses `POST /backfill { scripsID, candleInterval: "EOD", backfillPeriod }`.
   * The server returns only date-based OHLCV candles for the requested period.
   * Returns an array of `DayBar` sorted oldest-first.
   *
   * @param durationDays — 365 (1Y), 1825 (5Y), or 3650 (10Y).
   */
  public async fetchEodHistory(scripId: number, symbol: string, durationDays: number = 365): Promise<DayBar[]> {
    const backfillPeriod = this.mapBackfillPeriod(durationDays);
    const payload = {
      candleInterval: "EOD",
      backfillPeriod,
      scripsID: scripId,
    };
    console.log(`[Chart] POST backfill (EOD) payload:`, JSON.stringify(payload));
    try {
      const response = await axios.post("backfill", JSON.stringify(payload), {
        headers: { "Content-Type": "application/json" },
        timeout: 120000,
      });
      const preview = JSON.stringify(response.data)?.substring(0, 300) ?? "null";
      console.log(`[Chart] EOD backfill raw response.data:`, preview);

      if (this.isNoDataResponse(response.data)) {
        console.log(`[Chart] EOD backfill: no data for ${symbol}`);
        return [];
      }
      const bars = this.parseDayBars(response.data, symbol);
      console.log(`[Chart] EOD: ${bars.length} daily bars for ${symbol} (period=${backfillPeriod})`);
      return bars;
    } catch (error: any) {
      console.error(`[Chart] EOD backfill FAILED for ${symbol}:`, error?.message ?? error);
      return [];
    }
  }

  /**
   * Parse a raw server response into an array of `DayBar`.
   * Handles both array-of-arrays (`[datetime, o, h, l, c, vol, oi]`) and
   * array-of-objects formats, matching the server convention used by the 1M
   * backfill endpoint.  Date strings are interpreted as IST (+05:30) and
   * normalised to the IST calendar-day boundary.
   */
  private parseDayBars(raw: any, symbol: string): DayBar[] {
    // ── Locate the array inside the response ──────────────────────────────
    let dataArray: any[] = [];

    if (Array.isArray(raw)) {
      dataArray = raw;
    } else if (raw && typeof raw === "object") {
      const candidates = ["data", "Data", "candles", "Candles", "ohlcv", "result", "Result", "bars", "Bars"];
      for (const key of candidates) {
        if (Array.isArray(raw[key])) { dataArray = raw[key]; break; }
      }
      if (!dataArray.length) {
        for (const [, v] of Object.entries(raw)) {
          if (Array.isArray(v) && (v as any[]).length > 0) { dataArray = v as any[]; break; }
        }
      }
    }

    if (!dataArray.length) {
      console.warn(`[Chart] EOD parseDayBars for ${symbol}: no array found in response`);
      return [];
    }

    const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000; // 19 800 000 ms
    const DAY_MS = 86400000;
    const bars: DayBar[] = [];

    for (const item of dataArray) {
      let ts: number, open: number, high: number, low: number, close: number, vol: number, oi: number;

      if (Array.isArray(item)) {
        // [datetime, open, high, low, close, volume, OI?]
        const rawTs = item[0];
        if (typeof rawTs === "string") {
          // "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS" — treat as IST (+05:30)
          const isoStr = rawTs.length === 10
            ? rawTs + "T00:00:00+05:30"
            : rawTs.replace(" ", "T") + (rawTs.includes("+") ? "" : "+05:30");
          ts = new Date(isoStr).getTime();
          if (isNaN(ts)) ts = new Date(rawTs.replace(" ", "T")).getTime();
        } else {
          ts = typeof rawTs === "number" ? (rawTs >= 1e10 ? rawTs : rawTs * 1000) : 0;
        }
        open  = parseFloat(item[1]);
        high  = parseFloat(item[2]);
        low   = parseFloat(item[3]);
        close = parseFloat(item[4]);
        vol   = parseInt(String(item[5] ?? 0), 10);
        oi    = parseInt(String(item[6] ?? 0), 10);
      } else {
        const rawTs =
          item.timestamp ?? item.Timestamp ??
          item.time ?? item.Time ??
          item.date ?? item.Date ??
          item.t    ?? 0;
        if (typeof rawTs === "string") {
          const isoStr = rawTs.length === 10
            ? rawTs + "T00:00:00+05:30"
            : rawTs.replace(" ", "T") + (rawTs.includes("+") ? "" : "+05:30");
          ts = new Date(isoStr).getTime();
          if (isNaN(ts)) ts = new Date(rawTs.replace(" ", "T")).getTime();
        } else {
          ts = rawTs >= 1e10 ? rawTs : (rawTs * 1000);
        }
        open  = parseFloat(item.open  ?? item.Open  ?? item.o ?? 0);
        high  = parseFloat(item.high  ?? item.High  ?? item.h ?? 0);
        low   = parseFloat(item.low   ?? item.Low   ?? item.l ?? 0);
        close = parseFloat(item.close ?? item.Close ?? item.c ?? 0);
        vol   = parseInt(String(item.volume ?? item.Volume ?? item.vol ?? item.v ?? 0), 10);
        oi    = parseInt(String(item.oi ?? item.OI ?? item.openInterest ?? 0), 10);
      }

      if (!ts || isNaN(ts) || ts <= 0) continue;
      if (isNaN(open) || open <= 0) continue;

      // Normalise to UTC-equivalent of IST calendar-day start
      const istMs = ts + IST_OFFSET_MS;
      const dayStartIstMs = Math.floor(istMs / DAY_MS) * DAY_MS;
      const dayStartMs = dayStartIstMs - IST_OFFSET_MS; // UTC equivalent

      bars.push({
        timestamp: dayStartMs,
        open,
        high: isNaN(high) ? open : high,
        low: isNaN(low) ? open : low,
        close: isNaN(close) ? open : close,
        volume: isNaN(vol) ? 0 : vol,
        oi: isNaN(oi) ? 0 : oi,
      });
    }

    // Deduplicate by day and sort oldest-first
    const dayMap = new Map<number, DayBar>();
    for (const bar of bars) {
      dayMap.set(bar.timestamp, bar);
    }
    const sorted = Array.from(dayMap.values()).sort((a, b) => a.timestamp - b.timestamp);
    console.log(`[Chart] EOD parseDayBars for ${symbol}: ${sorted.length} daily bars`);
    if (sorted.length > 0) {
      const first = new Date(sorted[0].timestamp).toISOString().slice(0, 10);
      const last  = new Date(sorted[sorted.length - 1].timestamp).toISOString().slice(0, 10);
      console.log(`[Chart] EOD date range: ${first} → ${last}`);
    }
    return sorted;
  }
}

export const chartBackfillHandler = new ChartBackfillHandler();
